import { Button, ButtonGroup, Image, TextButton } from '@zillow/constellation';
import { Divider, Modal } from '@zillow/constellation-10';
import { useState } from 'react';
import {
  useUpsellWebApiHandler_clickUpsell,
  useUpsellWebApiHandler_optOutFromUpsell,
} from '@zg-rentals/ts-rins-client-react-query';
import type { ValueOf } from 'type-fest';

import UpsellBody from './UpsellBody';
import UpsellSurveyBody from './UpsellSurveyBody';
import ZillowInsuranceServicesLogo from '../../images/ZillowInsuranceServicesLogo.svg';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';
import { CLICK_LEASE_SURVEY_SUBMIT, CLICK_LEASE_UPSELL } from '../../utils/ClickstreamEvents';

import type { UpsellEntryPointType, UpsellInfo } from '../../types';

const UpsellSteps = {
  UPSELL: 'upsell',
  SURVEY: 'survey',
};

type UpsellSteps = ValueOf<typeof UpsellSteps>;

type UpsellModalProps = {
  isOpen: boolean;
  upsellEntryPointType: UpsellEntryPointType;
  upsellInfo: UpsellInfo;
  onClose: () => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const NO_RESPONSE_REASON = 'noResponse';

const UpsellModal = ({
  isOpen,
  upsellEntryPointType,
  upsellInfo,
  onClose,
  trackClickstreamEvent,
}: UpsellModalProps) => {
  const [currentStep, setCurrentStep] = useState<UpsellSteps>(UpsellSteps.UPSELL);
  const [surveyResponse, setSurveyResponse] = useState({ declineReason: '', order: 0 });

  const { mutate: clickUpsellMutation } = useUpsellWebApiHandler_clickUpsell();
  const { mutate: optOutFromUpsellMutation } = useUpsellWebApiHandler_optOutFromUpsell();
  const isUpsellStep = currentStep == UpsellSteps.UPSELL;

  const closeUpsell = () => {
    setCurrentStep(UpsellSteps.SURVEY);
  };

  const hideModal = () => {
    setCurrentStep(UpsellSteps.UPSELL);
    setSurveyResponse({ declineReason: '', order: 0 });
  };

  const closeModal = () => {
    hideModal();
    return onClose();
  };

  const onClickUpsell = () => {
    trackClickstreamEvent(CLICK_LEASE_UPSELL(upsellEntryPointType));
    clickUpsellMutation({
      postData: {
        upsellId: upsellInfo.upsellId,
        sessionGuid: '',
      },
    });

    window.open(upsellInfo.redirectUrl, '_blank');
    return closeModal();
  };

  const camelCaseToSnakeCase = (string: string) => string.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

  const submitSurvey = () => {
    const reason = surveyResponse.declineReason || NO_RESPONSE_REASON;
    const shouldHide = reason === 'alreadyEnrolled' || reason === 'wontEnrollZillow';
    const snakeCaseReason = camelCaseToSnakeCase(reason);
    trackClickstreamEvent(CLICK_LEASE_SURVEY_SUBMIT(snakeCaseReason, surveyResponse.order));
    optOutFromUpsellMutation({
      postData: {
        upsellId: upsellInfo.upsellId,
        reason,
        shouldHide,
      },
    });
    return closeModal();
  };

  const getBodyToRender = () => {
    if (isUpsellStep) {
      return <UpsellBody upsellInfo={upsellInfo} />;
    }

    return <UpsellSurveyBody onSurveyResponse={setSurveyResponse} />;
  };

  const getFooter = () => {
    if (isUpsellStep) {
      return (
        <ButtonGroup aria-label="Post lease signing upsell actions">
          <TextButton data-testid="upsell-modal-survey-no-thanks-btn" onClick={closeUpsell}>
            No thanks
          </TextButton>
          <Button data-testid="upsell-modal-click-upsell-btn" buttonType="primary" onClick={onClickUpsell}>
            Get started
          </Button>
        </ButtonGroup>
      );
    }

    return (
      <Button data-testid="upsell-modal-survey-submit-btn" buttonType="primary" onClick={submitSurvey}>
        Go to my leases
      </Button>
    );
  };

  return (
    <Modal.Root
      open={isOpen}
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{ escapeKey: false, outsidePress: false }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Image alt="Zillow Insurance Services Logo" src={ZillowInsuranceServicesLogo} />
            </Modal.Header>
            <Divider />
            <Modal.Body css={isUpsellStep ? { paddingBlock: 0, paddingInline: 0 } : {}}>{getBodyToRender()}</Modal.Body>
            <Divider />
            <Modal.Footer>{getFooter()}</Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default UpsellModal;
